import { useCallback, useEffect, useState } from "react";

import { staticReportsQueries } from "constants/reports";
import { useReportsActions } from "store";
import { useDealTitleSelector } from "store/selectors/deal-selectors/deal-selectors";
import { CashflowGraph, ReportCashflowSet, ReportPredictionTable } from "store/types";

interface IUseDealForecastOptions {
  /** Also fetch the prediction table (used by the Cashflow Forecast full view). */
  withTable?: boolean;
  /** When false the hook stays idle (e.g. forecasting flag off). */
  enabled?: boolean;
}

interface IUseDealForecast {
  loading: boolean;
  error: boolean;
  /** No forecastable data for this deal (S-3b). */
  isEmpty: boolean;
  graph: CashflowGraph | undefined;
  table: ReportPredictionTable | null;
  refetch: () => void;
}

const pickDefaultSet = (sets: ReportCashflowSet[]): ReportCashflowSet | undefined =>
  sets.find((set) => set.user_id === null) || sets[0];

/**
 * Fetches the deal-scoped cashflow forecast by reusing the portfolio cashflow
 * graph/table endpoints with a `deal_name` filter against the default set.
 * Scenario selection is out of scope — we always resolve the default set.
 */
export const useDealForecast = ({ withTable = false, enabled = true }: IUseDealForecastOptions = {}): IUseDealForecast => {
  const dealTitle = useDealTitleSelector();
  const { getCashflowSetsAction, getCashflowGraphAction, getCashflowTableAction } = useReportsActions();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [graph, setGraph] = useState<CashflowGraph>();
  const [table, setTable] = useState<ReportPredictionTable | null>(null);

  const fetchForecast = useCallback(async () => {
    if (!enabled || !dealTitle) return;

    setLoading(true);
    setError(false);

    try {
      const sets = await getCashflowSetsAction();
      const defaultSet = pickDefaultSet(sets);

      if (!defaultSet) {
        setGraph([]);
        setTable(null);
        return;
      }

      const baseParams = {
        set_id: defaultSet.id,
        filters: { deal_name: [dealTitle] },
        currency: staticReportsQueries.currency,
        dateRange: staticReportsQueries.dateRange,
        use_accounting_date: staticReportsQueries.use_accounting_date
      };

      const [graphData, tableData] = await Promise.all([
        getCashflowGraphAction({ ...baseParams, exchangeDate: staticReportsQueries.exchangeMethod }),
        withTable
          ? getCashflowTableAction({ ...baseParams, exchangeDate: staticReportsQueries.exchangeMethod, show_deal: true })
          : Promise.resolve(null)
      ]);

      setGraph(graphData);
      setTable(tableData);
    } catch (_e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [enabled, dealTitle, withTable, getCashflowSetsAction, getCashflowGraphAction, getCashflowTableAction]);

  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);

  // Empty when there is no graph, or every value across every period is zero/blank
  // (the endpoint returns all-zero rows for a deal with no forecastable measures).
  const isEmpty =
    !loading &&
    !error &&
    !!graph &&
    (graph.length === 0 || graph.every((row) => row.every((value) => !value)));

  return { loading, error, isEmpty, graph, table, refetch: fetchForecast };
};
